import { isOfflinePresentingStoredOn } from '@/services/offlinePresentingFlag'
import { isPlanBenefitScopedToPresentation } from '@/vuex/planBenefitScope'
import { clearSavedPresentations } from '@/utils/indexedDB/shared-frame-cache'
import { saveCachedCurrentUser, clearAllCachedData, getCachedCurrentUser } from './loading-page.utils'

const SESSION_REJECTED_STATUSES = [401, 403]
const PLAN_FIELDS = ['plan', 'paymentSource', 'cancel_at_period_end', 'myPlan', 'current_period_end']
const planFieldsOf = (user) => Object.fromEntries(
  PLAN_FIELDS.filter((field) => field in user).map((field) => [field, user[field]]),
)

const inFlightProfileLoads = new Set()

// A load that began from the saved profile finishes after the refresh and would overwrite it.
const trackProfileLoad = (load) => {
  inFlightProfileLoads.add(load)
  const forget = () => inFlightProfileLoads.delete(load)
  load.then(forget).catch(forget)
  return load
}

const fetchAndSaveCurrentUser = async (dispatch) => {
  try {
    const currentUser = await dispatch('fetchCurrentUserAction', null, { root: true })
    if (!currentUser) {
      // in case of fetchCurrentUserAction return null, clear cache and return null
      await clearAllCachedData()
      return null
    }
    await saveCachedCurrentUser(currentUser)
    return currentUser
  } catch (err) {
    const offlineOn = isOfflinePresentingStoredOn()
    if (!offlineOn || SESSION_REJECTED_STATUSES.includes(err?.status)) {
      await clearAllCachedData()
    }
    if (offlineOn && err?.status === 401) await clearSavedPresentations()
    throw err
  }
}

const actions = {

  fetchAndSaveCurrentUserAction({ dispatch }) {
    return trackProfileLoad(fetchAndSaveCurrentUser(dispatch))
  },

  async applyRefreshedProfileAction({ dispatch, rootState }, { profile, freshPlan } = {}) {
    await Promise.allSettled([...inFlightProfileLoads])
    const { currentUser } = rootState
    if (!currentUser || currentUser.id !== profile?.id) return
    const userWithoutPlan = { ...currentUser }
    PLAN_FIELDS.forEach((field) => delete userWithoutPlan[field])
    // freshPlan is null when the plan request failed with nothing to fall back on; the plan already shown then stands.
    const plan = freshPlan ?? planFieldsOf(currentUser)
    // A presentation-scoped benefit belongs to that presentation's owner, not to this account.
    const planBenefit = isPlanBenefitScopedToPresentation()
      ? (await getCachedCurrentUser())?.planBenefit
      : rootState.planBenefit
    const refreshedUser = {
      ...userWithoutPlan, ...profile, ...plan, planBenefit,
    }
    await dispatch('handleUserAndPlanLoaded', refreshedUser, { root: true })
    await saveCachedCurrentUser(refreshedUser)
  },

  async getCurrentUserWithCacheAction({ dispatch }) {
    const cachedUser = await getCachedCurrentUser()
    // always fetch to update the cache
    // but do not await on it such that the UI can be rendered as soon as possible
    const requestUser = dispatch('fetchAndSaveCurrentUserAction')
    if (cachedUser) {
      requestUser.catch(() => {})
      await dispatch('handleUserAndPlanLoaded', cachedUser, { root: true })
      return cachedUser
    }
    return requestUser
  },
}

export default actions
